import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message
} from 'antd';
import {
  SaveOutlined,
  SettingOutlined
} from '@ant-design/icons';
import api from '../../utils/api';

const { Title, Text } = Typography;

const settingFields = [
  [
    'cash_account',
    'บัญชีเงินสด',
    'ใช้เมื่อรับชำระเป็นเงินสด'
  ],
  [
    'bank_account',
    'บัญชีเงินฝากธนาคาร',
    'ใช้เมื่อรับหรือจ่ายด้วยการโอน'
  ],
  [
    'accounts_receivable',
    'บัญชีลูกหนี้การค้า',
    'ใช้เมื่อขายเชื่อและลูกค้าไม่ได้ผูกบัญชีเฉพาะ'
  ],
  [
    'accounts_payable',
    'บัญชีเจ้าหนี้การค้า',
    'ใช้เมื่อซื้อเชื่อและผู้ขายไม่ได้ผูกบัญชีเฉพาะ'
  ],
  [
    'sales_revenue',
    'บัญชีรายได้จากการขาย',
    'บัญชีรายได้เริ่มต้น'
  ],
  [
    'output_vat',
    'บัญชีภาษีขาย',
    'เครดิตภาษีมูลค่าเพิ่มจากการขาย'
  ],
  [
    'input_vat',
    'บัญชีภาษีซื้อ',
    'เดบิตภาษีมูลค่าเพิ่มจากการซื้อ'
  ],
  [
    'general_expense',
    'บัญชีค่าใช้จ่ายทั่วไป',
    'ใช้เมื่อผู้ขายไม่ได้ผูกบัญชีค่าใช้จ่ายเฉพาะ'
  ],
  [
    'inventory',
    'บัญชีสินค้าคงเหลือ',
    'ใช้บันทึกมูลค่าสินค้าคงเหลือ'
  ],
  [
    'cost_of_goods_sold',
    'บัญชีต้นทุนขาย',
    'ใช้บันทึกต้นทุนเมื่อขายสินค้า'
  ]
];

/**
 * แปลงข้อมูลบัญชีจาก API ให้อยู่ในรูปแบบเดียวกัน
 *
 * API ปัจจุบันส่ง:
 * id
 * CODE
 * NAME
 *
 * แต่รองรับชื่อ account_code และ account_name ไว้ด้วย
 * เผื่อมีการแก้ Backend ในภายหลัง
 */
const normalizeAccount = account => {
  const rawId =
    account?.id ??
    account?.account_id ??
    account?.accountId;

  const numericId = Number(rawId);

  return {
    id: Number.isNaN(numericId) ? rawId : numericId,

    code:
      account?.CODE ??
      account?.account_code ??
      account?.code ??
      '',

    name:
      account?.NAME ??
      account?.account_name ??
      account?.name ??
      '',

    allowPosting:
      account?.allow_posting === undefined ||
      account?.allow_posting === null
        ? true
        : Number(account.allow_posting) !== 0,

    isActive:
      account?.is_active === undefined ||
      account?.is_active === null
        ? true
        : Number(account.is_active) !== 0
  };
};

const normalizeAccountId = rawId => {
  if (
    rawId === null ||
    rawId === undefined ||
    rawId === ''
  ) {
    return undefined;
  }

  const numericId = Number(rawId);

  return Number.isNaN(numericId)
    ? rawId
    : numericId;
};

const AccountingSettingsPage = () => {
  const [form] = Form.useForm();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);

  const accountOptions = useMemo(() => {
    return accounts
      .filter(account => {
        return (
          account.allowPosting === true &&
          account.isActive === true &&
          account.id !== undefined &&
          account.id !== null
        );
      })
      .map(account => ({
        value: account.id,
        label: `${account.code} - ${account.name}`
      }));
  }, [accounts]);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);

      try {
        const [
          accountResponse,
          settingResponse,
          presetResponse
        ] = await Promise.all([
          api.get('/api/accounting/accounts'),
          api.get('/api/accounting/settings'),
          api.get('/api/accounting/posting-presets')
        ]);

        if (!mounted) {
          return;
        }

        const accountData = Array.isArray(
          accountResponse?.data?.data
        )
          ? accountResponse.data.data
          : [];

        const settingData = Array.isArray(
          settingResponse?.data?.data
        )
          ? settingResponse.data.data
          : [];

        const normalizedAccounts =
          accountData.map(normalizeAccount);

        const settingValues =
          settingData.reduce((result, item) => {
            if (!item?.setting_key) {
              return result;
            }

            result[item.setting_key] =
              normalizeAccountId(item.account_id);

            return result;
          }, {});

        setAccounts(normalizedAccounts);
        setPresets(presetResponse?.data?.data || []);

        form.setFieldsValue(settingValues);
      } catch (error) {
        console.error(
          'Load accounting settings error:',
          error
        );

        message.error(
          error.response?.data?.message ||
          'ไม่สามารถโหลดการตั้งค่าบัญชีได้'
        );
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [form]);

  const handleSave = async values => {
    setSaving(true);

    try {
      const settings = settingFields.map(
        ([settingKey, label, description]) => ({
          setting_key: settingKey,
          account_id: normalizeAccountId(
            values[settingKey]
          ),
          description: `${label}: ${description}`
        })
      );

      await api.put('/api/accounting/settings', {
        settings
      });

      message.success(
        'บันทึกการตั้งค่าการลงบัญชีแล้ว'
      );
    } catch (error) {
      console.error(
        'Save accounting settings error:',
        error
      );

      message.error(
        error.response?.data?.message ||
        'บันทึกการตั้งค่าไม่สำเร็จ'
      );
    } finally {
      setSaving(false);
    }
  };

  const savePreset = async (preset, values) => {
    setSaving(true);
    try {
      await api.put(`/api/accounting/posting-presets/${preset.id}`, { name: values.name, description: values.description, mappings: values.mappings });
      setPresets(current => current.map(item => item.id === preset.id ? { ...item, ...values } : item));
      message.success(`บันทึก Preset ${values.name} แล้ว`);
    } catch (error) { message.error(error.response?.data?.message || 'บันทึก Preset ไม่สำเร็จ'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space
        align="start"
        style={{ marginBottom: 20 }}
      >
        <SettingOutlined
          style={{
            fontSize: 28,
            color: '#1677ff'
          }}
        />

        <div>
          <Title
            level={2}
            style={{ margin: 0 }}
          >
            ตั้งค่าการลงบัญชีอัตโนมัติ
          </Title>

          <Text type="secondary">
            กำหนดผังบัญชีเริ่มต้นสำหรับการขาย
            การรับชำระ การซื้อ และภาษี
          </Text>
        </div>
      </Space>

      <Card
        bordered={false}
        style={{ borderRadius: 14 }}
      >
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
          >
            <Row gutter={[20, 8]}>
              {settingFields.map(
                ([
                  settingKey,
                  label,
                  description
                ]) => (
                  <Col
                    xs={24}
                    md={12}
                    key={settingKey}
                  >
                    <Form.Item
                      name={settingKey}
                      label={label}
                      extra={description}
                      rules={[
                        {
                          required: true,
                          message: `กรุณาเลือก${label}`
                        }
                      ]}
                    >
                      <Select
                        showSearch
                        allowClear
                        placeholder={`เลือก${label}`}
                        optionFilterProp="label"
                        options={accountOptions}
                        filterOption={(
                          input,
                          option
                        ) => {
                          return String(
                            option?.label || ''
                          )
                            .toLowerCase()
                            .includes(
                              input.toLowerCase()
                            );
                        }}
                        notFoundContent={
                          loading
                            ? 'กำลังโหลดข้อมูล...'
                            : 'ไม่พบรายการบัญชี'
                        }
                      />
                    </Form.Item>
                  </Col>
                )
              )}
            </Row>

            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={loading}
              size="large"
            >
              บันทึกการตั้งค่า
            </Button>
          </Form>
        </Spin>
      </Card>

      <Title level={3} style={{ marginTop: 28 }}>Preset ผังบัญชีตามประเภทธุรกิจ</Title>
      <Text type="secondary">Preset จะถูกนำไปเสนอใน Modal ก่อนลงบัญชี ผู้ใช้ยังสามารถเปลี่ยนบัญชีเฉพาะรายการได้</Text>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {presets.map(preset => <Col xs={24} xl={12} key={preset.id}>
          <Card title={preset.name} extra={<Text code>{preset.preset_code}</Text>}>
            <Form layout="vertical" initialValues={{ name: preset.name, description: preset.description, mappings: preset.mappings || {} }} onFinish={values => savePreset(preset, values)}>
              <Form.Item name="name" label="ชื่อ Preset" rules={[{ required: true, whitespace: true, message: 'กรุณาระบุชื่อ Preset' }]}>
                <Input maxLength={150} showCount placeholder="ระบุชื่อ Preset" />
              </Form.Item>
              <Row gutter={12}>{settingFields.map(([key, label]) => <Col xs={24} md={12} key={key}>
                <Form.Item name={['mappings', key]} label={label}><Select allowClear showSearch optionFilterProp="label" options={accountOptions} placeholder="ใช้ค่ากลางของระบบ" /></Form.Item>
              </Col>)}</Row>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>บันทึก Preset</Button>
            </Form>
          </Card>
        </Col>)}
      </Row>
    </div>
  );
};

export default AccountingSettingsPage;
